import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StaffingBadge } from "@/components/StaffingBadge";
import { Navigation, ExternalLink, Layers } from "lucide-react";

const BASEMAPS = {
  standard: { label: "Map", layers: [{ url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap" }] },
  satellite: { label: "Satellite", layers: [{ url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" }] },
  hybrid: { label: "Hybrid", layers: [
    { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" },
    { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", attribution: "" },
  ] },
  dark: { label: "Dark", layers: [
    { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" },
    { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}", attribution: "" },
  ] },
};

const pin = (kind, colour) => {
  const base = kind === "shift" ? "#0d9488" : "#EA731D";
  const c = colour || base;
  return L.divIcon({
    className: "",
    html: `<div style="background:${c};width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.45)"><span style="display:block;width:9px;height:9px;background:#fff;border-radius:50%;margin:7px auto"></span></div>`,
    iconSize: [28, 28], iconAnchor: [14, 26], popupAnchor: [0, -24],
  });
};

const queuePin = L.divIcon({
  className: "",
  html: `<div style="background:#7C3AED;width:26px;height:26px;border-radius:6px;transform:rotate(45deg);border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.45)"></div>`,
  iconSize: [26, 26], iconAnchor: [13, 13], popupAnchor: [0, -14],
});

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    if (points.length) {
      map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [40, 40], maxZoom: 14 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

export default function MapView({ events, onOpenDetails, queued = [] }) {
  const { t } = useI18n();
  const [baseMap, setBaseMap] = useState("standard");
  const located = (events || []).filter((e) => typeof e.lat === "number" && typeof e.lng === "number");
  const missing = (events || []).filter((e) => !(typeof e.lat === "number" && typeof e.lng === "number"));
  const center = located.length ? [located[0].lat, located[0].lng] : [53.4808, -2.2426];

  return (
    <div className="space-y-2">
      <div className="rounded-xl overflow-hidden border border-slate-200 relative isolate" style={{ height: "calc(100vh - 150px)", minHeight: 520 }} data-testid="calendar-map">
        <div className="absolute top-2 right-2 z-[1000] flex rounded-lg border border-slate-200 overflow-hidden shadow-sm bg-white" data-testid="calmap-basemap-switch">
          {Object.entries(BASEMAPS).map(([k, v]) => (
            <button key={k} data-testid={`calmap-base-${k}`} onClick={() => setBaseMap(k)}
              className={`px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1 ${baseMap === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {k === "standard" && <Layers className="h-3 w-3" />}{t(`map.base.${k}`, v.label)}
            </button>
          ))}
        </div>
        <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          {BASEMAPS[baseMap].layers.map((l, i) => (
            <TileLayer key={`${baseMap}-${i}`} attribution={l.attribution} url={l.url} />
          ))}
          <FitBounds points={[...located, ...queued.filter((q) => typeof q.geo?.lat === "number").map((q) => q.geo)]} />
          <MarkerClusterGroup chunkedLoading maxClusterRadius={45} disableClusteringAtZoom={15} spiderfyOnMaxZoom showCoverageOnHover={false}>
            {located.map((e) => (
              <Marker key={`${e.kind}-${e.id}`} position={[e.lat, e.lng]} icon={pin(e.kind, e.workerColour)}>
                <Popup>
                  <div style={{ minWidth: 210 }} data-testid={`map-popup-${e.id}`}>
                    <div className="flex items-center gap-1 mb-1 flex-wrap">
                      <Badge className={e.kind === "shift" ? "bg-teal-600" : "bg-brand"}>{e.kind === "shift" ? t("connect.label", "iClean Connect") : t("field.label", "iClean Field")}</Badge>
                      {e.kind === "job" && <Badge variant="outline" className="border-brand/40 text-brand">{t(`jt.${e.jobType}`, e.jobType)}</Badge>}
                    </div>
                    <p className="font-semibold text-sm text-slate-900">{e.title}</p>
                    <p className="text-xs text-slate-600">{e.address || e.propertyAddress || "—"}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{e.date} · {e.startTime}–{e.endTime}</p>
                    {e.kind === "job"
                      ? (e.workers?.length ? <p className="text-xs text-slate-700 mt-0.5">{e.workers.map((w) => w.name).join(", ")}</p> : <p className="text-xs text-slate-400 mt-0.5">{t("job.unassigned", "Unassigned")}</p>)
                      : (e.workerName && <p className="text-xs text-slate-700 mt-0.5">{e.workerName}</p>)}
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge variant="outline">{t(`st.${e.status}`, e.status)}</Badge>
                      {e.kind === "job" && (e.requiredWorkerCount || 1) > 1 && <StaffingBadge assigned={e.workers?.length || 0} required={e.requiredWorkerCount} testid={`map-staffing-${e.id}`} />}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="h-7 text-xs bg-brand hover:bg-brand-dark" data-testid={`map-open-${e.id}`} onClick={() => onOpenDetails(e)}><ExternalLink className="h-3 w-3 mr-1" />{t("map.open", "Open")}</Button>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${e.lat},${e.lng}`} target="_blank" rel="noreferrer" data-testid={`map-directions-${e.id}`} className="inline-flex items-center h-7 px-2 text-xs rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50"><Navigation className="h-3 w-3 mr-1" />{t("job.directions", "Get directions")}</a>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
          {queued.filter((q) => typeof q.geo?.lat === "number" && typeof q.geo?.lng === "number").map((q) => (
            <Marker key={`q-${q.id}`} position={[q.geo.lat, q.geo.lng]} icon={queuePin} data-testid={`calmap-queue-${q.id}`}>
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <Badge className="bg-violet-600 mb-1">Queued</Badge>
                  <p className="font-semibold text-sm text-slate-900">{q.address}</p>
                  <p className="text-xs text-slate-600">{q.clientName}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Needs {Math.round((q.durationMinutes || 120) / 6) / 10}h · drag it from the queue onto the calendar to book</p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {missing.length > 0 && (
        <p className="text-xs text-slate-400" data-testid="map-missing-count">{missing.length} {t("map.noLocation", "item(s) without map coordinates (address on file)")}</p>
      )}
    </div>
  );
}
